/**
 * Impact metrics.
 *
 * A "28 calendar years since 1998" counter used to sit at the head of this list.
 * It is gone. 2026 minus 1998 is 28 only if the organisation was founded early
 * enough in 1998, and HISCUS has not published the founding date, so the figure
 * was arithmetic presented as a fact. Its own context line admitted as much,
 * which is not something a reader should have to notice. The site says
 * "since 1998" in prose instead, which is the part that is actually documented.
 *
 * Only figures published by the organisation appear here, each with the period
 * it refers to. Legacy figures whose reporting period is unclear (420+ health
 * camp participants, 175 women in computer education, 18 CCID learners, 200+
 * Nirantar Shiksha participants) are deliberately NOT used as headline counters
 *, see CONTENT_CONFIRMATION.md.
 */

export type ImpactMetric = {
  id: string;
  value: number;
  /**
   * Only ever "+", and only where the source says "at least". 28 years and 11
   * girls are exact counts, so they carry no plus: adding one would overstate
   * figures the organisation reports precisely.
   */
  suffix?: "+";
  label: string;
  context: string;
  asOf?: string;
  source?: string;
  /** Wording must be signed off by the client before launch. */
  requiresFinalApproval?: boolean;
};

export const headlineMetrics: ImpactMetric[] = [
    {
    id: "nestling-girls",
    value: 11,
    label: "Girls supported at Sarangkot Nestling Home",
    context: "Sarangkot, as reported for 2025",
    asOf: "2025",
    source: "https://himshikharanepal.org.np/homepage/what-we-do/the-sarangkot-nestling-trusthome/",
  },
  {
    id: "water-households",
    value: 60,
    suffix: "+",
    label: "Households with restored water access",
    context: "Bethanchowk water tank, completed 9 July 2025",
    asOf: "2025",
    source:
      "https://himshikharanepal.org.np/homepage/what-we-do/health-camp-program/bethanchowck-water-tank-construction-project/",
  },
  {
    id: "access-path",
    value: 700,
    label: "Metres of community access path built",
    // No completion year is published, so the caveat lives in the visible context
    // line. Leaving asOf unset keeps the screen-reader sentence grammatical.
    context: "Sarangkot; completion year not yet published",
    asOf: undefined,
    source: "https://himshikharanepal.org.np/homepage/what-we-do/construction-and-renovation/",
  },
];

export type OutcomeRecord = {
  id: string;
  headline: string;
  detail: string;
  pillar: string;
  location: string;
  year: string;
  date?: string;
};

/** Dated, documented outcomes used on the Impact page filters. */
export const outcomeRecords: OutcomeRecord[] = [
  {
    id: "nestling-home-completed",
    headline: "Sarangkot Nestling Home completed",
    detail:
      "The home was completed on 13 April 2018 and is operated in partnership with The Nestling Trust UK.",
    pillar: "Children & Education",
    location: "Sarangkot",
    year: "2018",
    date: "13 April 2018",
  },
  {
    id: "thapathali-flood",
    headline: "Thapathali flood response",
    detail:
      "Clothing support for affected children and a community water-storage tank following the floods.",
    pillar: "Health, Relief & Community Resilience",
    location: "Thapathali, Kathmandu",
    year: "2024",
    date: "8–9 October 2024",
  },
  {
    id: "bethanchowk-health-post",
    headline: "Bethanchowk health post support",
    detail: "Supplies and support delivered to the local health post.",
    pillar: "Health, Relief & Community Resilience",
    location: "Bethanchowk",
    year: "2025",
    date: "1 January 2025",
  },
  {
    id: "bethanchowk-dignity",
    headline: "Empowering with Dignity, Bethanchowk",
    detail: "18 women took part in a menstrual health and reusable pad-making session.",
    pillar: "Women & Girls",
    location: "Bethanchowk",
    year: "2025",
    date: "28 January 2025",
  },
  {
    id: "bhaktapur-psychosocial",
    headline: "Psychosocial wellbeing session, Bhaktapur",
    detail: "20 participants joined a psychosocial wellbeing and stress-management session.",
    pillar: "Women & Girls",
    location: "Bhaktapur",
    year: "2025",
    date: "17 March 2025",
  },
  {
    id: "ropeway-sadak-dignity",
    headline: "Empowering with Dignity, Ropeway Sadak",
    detail: "25 participants joined a menstrual health education and pad-making session.",
    pillar: "Women & Girls",
    location: "Ropeway Sadak",
    year: "2025",
    date: "13 June 2025",
  },
  {
    id: "bethanchowk-water-tank",
    headline: "Bethanchowk water tank completed",
    detail:
      "Construction ran from October 2024 to 9 July 2025, restoring reliable water access for at least 60 households.",
    pillar: "Health, Relief & Community Resilience",
    location: "Bethanchowk",
    year: "2025",
    date: "October 2024 – 9 July 2025",
  },
];

export const impactYears = ["All years", "2025", "2024", "2018"] as const;
